import { FC, useEffect, useState, useMemo } from 'react'
import {
  Alert, Avatar, Box, Button, Chip, CircularProgress, Dialog, DialogActions,
  DialogContent, DialogTitle, IconButton, Grid2, Paper, Table, TableBody, 
  TableCell, TableHead, TableRow, alpha, TextField, Tooltip, Typography, 
  Popover, Divider, TablePagination, Select, MenuItem, FormControl, Stack, Skeleton
} from '@mui/material'
import { useTheme } from '@mui/material/styles'
import {
  MdEdit, MdRefresh, MdOutlineCalendarToday
} from 'react-icons/md'
import api from '../../../../Services/api'
import { Loader } from '../../../../Components/Loader'
import { ICategoryEntity } from '../../../../Services/api/categories/categories.interface'

const PRODUCT_PRODUCTION_TYPES_ENDPOINT = '/product-production-types'

// ─── Tipos ───────────────────────────────────────────────────────────────────

interface IProductionType {
  id: string;
  name: string;
  production: number | null;
  sheet: number | null;
  logistics: number | null;
}

interface IProduct {
  id: string;
  name: string;
  code: string; // SKU
  freteHeroId: string | null;
  productionTypeId: string | null;
  productionType?: IProductionType | null;
  images?: Array<{ link: string }>;
  maskCodeDetails?: { generatedMaskName: string };
  maskCode?: string;
  categories?: any[];
}

export const PrazosFluxosHCP: FC = () => {
  const theme = useTheme()

  const [products, setProducts] = useState<IProduct[]>([])
  const [loading, setLoading] = useState(false)
  const [sincronizando, setSincronizando] = useState(false)
  const [msgSinc, setMsgSinc] = useState<{ tipo: 'success' | 'error' | 'warning'; texto: string } | null>(null)

  // Paginação
  const [page, setPage] = useState<number>(0)
  const [limit, setLimit] = useState<number>(20)
  const [total, setTotal] = useState<number>(0)
  
  // Filtros
  const [textoBusca, setTextoBusca] = useState<string>('')
  const [categories, setCategories] = useState<ICategoryEntity[]>([])
  const [selectedCategoryId, setSelectedCategoryId] = useState<string | undefined>()
  const [categoriesLoading, setCategoriesLoading] = useState(false)

  // Popover: Visualização de Fluxos
  const [anchorElPrazos, setAnchorElPrazos] = useState<HTMLElement | null>(null)
  const [produtoPrazosSelecionado, setProdutoPrazosSelecionado] = useState<IProduct | null>(null)

  // Dialog: Edição de Tipo de Produção do Produto
  const [editOpen, setEditOpen] = useState(false)
  const [editingProduto, setEditingProduto] = useState<IProduct | null>(null)
  
  // Estados para o Form (F2, F3, F4)
  const [editF2, setEditF2] = useState<number>(0) // Sheet (Montagem)
  const [editF3, setEditF3] = useState<number>(0) // Production
  const [editF4, setEditF4] = useState<number>(0) // Logistics
  const [salvandoPrazo, setSalvandoPrazo] = useState(false)

  const [productionTypes, setProductionTypes] = useState<IProductionType[]>([])

  const fetchData = async () => {
    setLoading(true)
    setMsgSinc(null)
    try {
      const currentPage = page + 1
      const [res, typeRes] = await Promise.all([
        api.get('/products', { params: { 
          page: currentPage, 
          offset: limit, 
          searchBy: textoBusca,
          categoryId: selectedCategoryId 
        } }),
        api.get(PRODUCT_PRODUCTION_TYPES_ENDPOINT).catch(() => ({ data: { data: [] } }))
      ])
      
      const list = Array.isArray(res.data?.data) ? res.data.data : (Array.isArray(res.data) ? res.data : [])
      const typesList = Array.isArray(typeRes.data?.data) ? typeRes.data.data : (Array.isArray(typeRes.data) ? typeRes.data : [])
      
      setProducts(list)
      setProductionTypes(typesList)
      setTotal(res.data?.totalRegisters || res.data?.total || list.length)

    } catch (e: any) {
      setMsgSinc({ tipo: 'error', texto: e?.message || 'Erro ao carregar produtos do Hub.' })
      setProducts([])
    } finally {
      setLoading(false)
    }
  }

  const loadCategories = async () => {
    try {
      setCategoriesLoading(true)
      const res = await api.get('/categories', { params: { getAll: true } })
      const list = res.data?.data || res.data || []
      setCategories(list)
    } catch (e) {
      console.error("Erro ao carregar categorias", e)
    } finally {
      setCategoriesLoading(false)
    }
  }

  useEffect(() => {
    fetchData()
  }, [page, limit, selectedCategoryId])

  useEffect(() => {
    loadCategories()
  }, [])

  const handleSearch = () => {
    setPage(0)
    fetchData()
  }

  const handleLinkProductionType = async (productId: string, newTypeId: string) => {
    setLoading(true)
    try {
      await api.patch(`/products/${productId}`, { productionTypeId: newTypeId })
      
      setProducts(prev => prev.map(prod => {
         if(prod.id === productId) {
             return { ...prod, productionTypeId: newTypeId, productionType: productionTypes.find(t => t.id === newTypeId) }
         }
         return prod
      }))
      setMsgSinc({ tipo: 'success', texto: 'Tipo de produção vinculado ao produto.' })
    } catch(err: any) {
      alert("Erro ao vincular tipo: " + (err?.response?.data?.message || err?.message))
    } finally {
      setLoading(false)
    }
  }

  const sincronizarFreteHero = async () => {
    setSincronizando(true)
    setMsgSinc(null)
    try {
      await api.post('/frete-hero/products/sync')
      setMsgSinc({ tipo: 'success', texto: 'Sincronização acionada com sucesso no Frete Hero.' })
      fetchData()
    } catch (e: any) {
      setMsgSinc({ tipo: 'error', texto: `Falha na sincronização: ${e?.message || 'Erro.'}` })
    } finally {
      setSincronizando(false)
    }
  }

  const abrirEditarPrazo = (prod: IProduct) => {
    if (!prod.productionTypeId) {
      alert("Este produto ainda não está vinculado a nenhum 'Tipo de Produção'. Você não pode configurar dias sem vinculá-lo antes no painel do produto.")
      return
    }

    setEditingProduto(prod)
    const pt = prod.productionType || productionTypes.find(t => t.id === prod.productionTypeId)

    setEditF2(Number(pt?.sheet) || 0)
    setEditF3(Number(pt?.production) || 0)
    setEditF4(Number(pt?.logistics) || 0)

    setEditOpen(true)
  }

  const salvarTipoDeProducao = async () => {
    if (!editingProduto || !editingProduto.productionTypeId) return
    setSalvandoPrazo(true)

    try {
      await api.put(`${PRODUCT_PRODUCTION_TYPES_ENDPOINT}/${editingProduto.productionTypeId}`, {
        sheet: editF2,
        production: editF3,
        logistics: editF4
      })

      // Atualiza localmente
      setProducts(prev => prev.map(p => {
        if (p.productionTypeId === editingProduto.productionTypeId) {
          return {
            ...p,
            productionType: {
              ...p.productionType!,
              sheet: editF2,
              production: editF3,
              logistics: editF4
            }
          }
        }
        return p
      }))
      
      setEditOpen(false)
      setMsgSinc({ tipo: 'success', texto: `Tipo de Produção "${editingProduto.productionType?.name}" atualizado! Todos os produtos associados serão afetados.` })
    } catch (e: any) {
      alert(`Erro ao salvar no backend: ${e?.message || 'Erro Desconhecido'}`)
    } finally {
      setSalvandoPrazo(false)
    }
  }

  return (
    <Box sx={{ p: 1 }}>
      <Paper variant="outlined" sx={{ p: 2, mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 2, flexWrap: 'wrap', gap: 1 }}>
          <Box>
            <Typography variant="h6" fontWeight={600}>
              Prazos e Fluxos de Produção (Total: {total})
            </Typography>
            <Typography variant="caption" color="text.secondary">
               Painel nativo. Os Prazos são gerenciados pelos Tipos de Produção. Editar um fluxo na listagem ou modal atualizará o Tipo em si.
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <Tooltip title="Recarregar">
              <IconButton size="small" onClick={fetchData} disabled={loading || sincronizando}>
                <MdRefresh />
              </IconButton>
            </Tooltip>
            <Button
              variant="contained" size="small"
              startIcon={sincronizando ? <CircularProgress size={14} color="inherit" /> : <MdRefresh />}
              onClick={sincronizarFreteHero}
              disabled={sincronizando || loading}
            >
              Forçar Sincronia de Prazos (Frete Hero)
            </Button>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 3, flexWrap: 'wrap' }}>
          <TextField
            size="small"
            placeholder="Buscar produto por nome ou SKU..."
            value={textoBusca}
            onChange={(e) => setTextoBusca(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') handleSearch() }}
            sx={{ flexGrow: 1, maxWidth: 400 }}
          />
          <Button variant="outlined" size="small" onClick={handleSearch} disabled={loading}>
            Buscar
          </Button>
        </Box>

        {/* {(categoriesLoading || categories.length > 0) && (
          <Box sx={{ mb: 3 }}>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ gap: 1 }}>
              {categoriesLoading ? (
                Array.from({ length: 5 }).map((_, idx) => (
                  <Skeleton key={idx} variant="rectangular" width={80} height={28} sx={{ borderRadius: '14px' }} />
                ))
              ) : (
                categories.map((cat) => (
                  <Chip
                    key={cat.id || cat.name}
                    label={cat.name}
                    size="small"
                    onClick={() => {
                      const newCat = selectedCategoryId === cat.id ? undefined : cat.id
                      setSelectedCategoryId(newCat)
                      setPage(0)
                    }}
                    color={selectedCategoryId === cat.id ? 'primary' : 'default'}
                    variant={selectedCategoryId === cat.id ? 'filled' : 'outlined'}
                    sx={{
                      fontWeight: selectedCategoryId === cat.id ? 700 : 400,
                      fontSize: '0.75rem',
                      '&:hover': {
                        backgroundColor: selectedCategoryId === cat.id ? theme.palette.primary.main : alpha(theme.palette.primary.main, 0.04),
                      }
                    }}
                  />
                ))
              )}
            </Stack>
          </Box>
        )} */}

        {msgSinc && <Alert severity={msgSinc.tipo} sx={{ mb: 2 }} onClose={() => setMsgSinc(null)}>{msgSinc.texto}</Alert>}

        {loading ? (
          <Loader />
        ) : (
          <Box sx={{ overflowX: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Produto</TableCell>
                  <TableCell>Máscara / Categoria</TableCell>
                  <TableCell align="center">Prazo Site (Produção + Montagem)</TableCell>
                  <TableCell align="center">Fluxos Relacionados</TableCell>
                  <TableCell align="center">Ações</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {products.map((p) => {
                  const pt = p.productionType || productionTypes.find(t => t.id === p.productionTypeId)
                  const sheet = Number(pt?.sheet) || 0
                  const prod = Number(pt?.production) || 0
                  const logi = Number(pt?.logistics) || 0
                  const prazoSiteVisual = sheet + prod

                  return (
                  <TableRow key={p.id}>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
                        <Avatar variant="rounded" src={p.images?.[0]?.link || undefined} sx={{ width: 44, height: 44, border: '1px solid', borderColor: 'divider' }} />
                        <Box sx={{ maxWidth: 280 }}>
                          <Typography variant="body2" fontWeight={800} color="text.primary" sx={{ lineHeight: 1.2, mb: 0.5 }}>{p.name}</Typography>
                          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                            <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600 }}>SKU: {p.code}</Typography>
                          </Box>
                        </Box>
                      </Box>
                    </TableCell>

                    <TableCell>
                      <Typography variant="body2" fontWeight={600} color="text.secondary">
                        {p.maskCodeDetails?.generatedMaskName || p.maskCode || '—'}
                      </Typography>
                      <FormControl size="small" sx={{ mt: 0.5, minWidth: 160 }}>
                        <Select
                          displayEmpty
                          value={p.productionTypeId || ''}
                          onChange={(e) => handleLinkProductionType(p.id, e.target.value as string)}
                          sx={{ 
                             height: 28, fontSize: '0.75rem', fontWeight: 700, 
                             bgcolor: p.productionTypeId ? alpha(theme.palette.primary.main, 0.08) : alpha(theme.palette.error.main, 0.08),
                             color: p.productionTypeId ? theme.palette.primary.main : theme.palette.error.main,
                             '.MuiOutlinedInput-notchedOutline': { border: 'none' }
                          }}
                        >
                          <MenuItem value="" disabled>-- S/ Tipo de Produção --</MenuItem>
                          {productionTypes.map(t => (
                            <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </TableCell>

                    <TableCell align="center">
                      <Chip 
                        label={`${pt ? prazoSiteVisual : 0} dias`} 
                        size="small" 
                        sx={{ 
                          fontWeight: 900, 
                          bgcolor: alpha(theme.palette.success.main, 0.1),
                          color: theme.palette.success.main,
                          border: `1px solid ${alpha(theme.palette.success.main, 0.2)}`,
                        }} 
                      />
                    </TableCell>

                    <TableCell align="center">
                      <Tooltip title={`Ver detalhamento: ${pt?.name || 'N/A'}`}>
                        <Box 
                          onClick={(e) => { if(pt) { setAnchorElPrazos(e.currentTarget as any); setProdutoPrazosSelecionado(p); } }}
                          sx={{ 
                            display: 'inline-flex', alignItems: 'center', gap: 0.5, 
                            cursor: pt ? 'pointer' : 'default', opacity: pt ? 1 : 0.4,
                            bgcolor: alpha(theme.palette.info.main, 0.05), px: 1, py: 0.3,
                            borderRadius: 1, border: '1px solid', borderColor: alpha(theme.palette.info.main, 0.1),
                          }}
                        >
                          <Typography variant="caption" fontWeight={800} color="info.main">
                            {(pt?.sheet||0)}M + {(pt?.production||0)}P + {(pt?.logistics||0)}L
                          </Typography>
                          <MdOutlineCalendarToday size={12} color={theme.palette.info.main} />
                        </Box>
                      </Tooltip>
                    </TableCell>

                    <TableCell align="center">
                      <Tooltip title="Editar Prazos Universais deste Tipo">
                         <IconButton size="small" onClick={() => abrirEditarPrazo(p)} color="primary"><MdEdit /></IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                )})}

                {products.length === 0 && (
                  <TableRow>
                     <TableCell colSpan={5} align="center" sx={{ py: 3 }}>
                       Nenhum produto encontrado.
                     </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
             <TablePagination
                component="div"
                count={total}
                page={page}
                onPageChange={(e, v) => setPage(v)}
                rowsPerPage={limit}
                onRowsPerPageChange={(e) => { setLimit(parseInt(e.target.value, 10)); setPage(0); }}
                labelRowsPerPage="Exibir:"
             />
          </Box>
        )}
      </Paper>

      <Popover
        open={Boolean(anchorElPrazos)}
        anchorEl={anchorElPrazos}
        onClose={() => { setAnchorElPrazos(null); setProdutoPrazosSelecionado(null); }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        transformOrigin={{ vertical: 'top', horizontal: 'center' }}
        PaperProps={{ sx: { p: 2, minWidth: 260, borderRadius: 2 } }}
      >
        {(() => {
          if (!produtoPrazosSelecionado) return null;
          const pt = produtoPrazosSelecionado.productionType || productionTypes.find(t => t.id === produtoPrazosSelecionado.productionTypeId);
          if (!pt) return null;
          return (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
               <Box sx={{ borderBottom: '1px solid', borderColor: 'divider', pb: 1, mb: 1 }}>
                 <Typography variant="subtitle2" fontWeight={900} sx={{ letterSpacing: -0.5 }}>TIPO: {pt.name.toUpperCase()}</Typography>
                 <Typography variant="caption" color="text.secondary">Válido para o SKU: {produtoPrazosSelecionado.code}</Typography>
               </Box>
               
               <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  {[
                    { label: 'F2: Montagem/Sheet', value: Number(pt.sheet) || 0, color: '#9c27b0' },
                    { label: 'F3: Produção', value: Number(pt.production) || 0, color: '#4caf50' },
                    { label: 'F4: Logística', value: Number(pt.logistics) || 0, color: '#ff9800' }
                  ].map((item) => (
                    <Box key={item.label} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: item.color }} />
                        <Typography variant="caption" fontWeight={700} color="text.secondary">{item.label}</Typography>
                      </Box>
                      <Typography variant="caption" fontWeight={900} sx={{ bgcolor: alpha(item.color, 0.1), color: item.color, px: 1, borderRadius: 1 }}>
                        {item.value || 0} dias
                      </Typography>
                    </Box>
                  ))}
               </Box>

               <Box sx={{ 
                 mt: 1, p: 1.5, borderRadius: 2, 
                 bgcolor: alpha(theme.palette.success.main, 0.05), border: '1px solid', borderColor: alpha(theme.palette.success.main, 0.1),
                 display: 'flex', justifyContent: 'space-between', alignItems: 'center'
               }}>
                 <Typography variant="caption" fontWeight={800} color="success.main">PRAZO TOTAL</Typography>
                 <Typography variant="subtitle2" fontWeight={900} color="success.main">
                   {(Number(pt.sheet) || 0) + (Number(pt.production) || 0) + (Number(pt.logistics) || 0)} dias
                 </Typography>
               </Box>
            </Box>
          );
        })()}
      </Popover>

      {/* Dialog de Edição Nativa via ProductionTypes */}
      <Dialog open={editOpen} onClose={() => setEditOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ pb: 1, lineHeight: 1.2 }}>
           Editar Prazos<br/>
           {editingProduto?.productionTypeId && <Typography variant="caption" fontWeight={700} color="primary">{(editingProduto.productionType || productionTypes.find(t => t.id === editingProduto.productionTypeId))?.name}</Typography>}
        </DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 3, pt: 2 }}>
           <Alert severity="warning" sx={{ mb: 1, py: 0 }}>
              <Typography variant="caption">Qualquer mudança afetará todos os produtos deste Tipo de Produção.</Typography>
           </Alert>

           <Box>
             <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{ textTransform: 'uppercase', mb: 1.5, display: 'block' }}>Configuração de Dias</Typography>
             
             {/* Prazo Site Total -> Mostra a Soma */}
             <TextField 
                label="Prazo Site (Produção + Montagem)" 
                type="number" 
                value={(editF2 || 0) + (editF3 || 0)} 
                disabled 
                fullWidth size="small" sx={{ mb: 2 }} 
             />
             
             <Grid2 container spacing={2}>
                <Grid2 size={6}><TextField label="F1: Arte" type="number" value={0} disabled fullWidth size="small" /></Grid2>
                <Grid2 size={6}><TextField label="F2: Montagem" type="number" value={editF2} onChange={e => setEditF2(Number(e.target.value) < 0 ? 0 : Number(e.target.value))} fullWidth size="small" /></Grid2>
                <Grid2 size={6}><TextField label="F3: Produção" type="number" value={editF3} onChange={e => setEditF3(Number(e.target.value) < 0 ? 0 : Number(e.target.value))} fullWidth size="small" /></Grid2>
                <Grid2 size={6}><TextField label="F4: Logística" type="number" value={editF4} onChange={e => setEditF4(Number(e.target.value) < 0 ? 0 : Number(e.target.value))} fullWidth size="small" /></Grid2>
             </Grid2>
           </Box>

           <Divider />


        </DialogContent>
        {salvandoPrazo && (
          <Alert severity="info" sx={{ mx: 2, mb: 1 }}>Aguarde... Enviando modificações pro Hub Principal.</Alert>
        )}
        <DialogActions>
           <Button onClick={() => setEditOpen(false)} disabled={salvandoPrazo}>Cancelar</Button>
           <Button variant="contained" onClick={salvarTipoDeProducao} disabled={salvandoPrazo}>
             {salvandoPrazo ? 'Salvando...' : 'Salvar Alterações'}
           </Button>
        </DialogActions>
      </Dialog>

    </Box>
  )
}
